import React, { useMemo, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { AddAlt } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications-api';
import { useQueryClient } from '@mint-lib/query-client';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import {
  BaseFile,
  FileUploader,
  FileUploaderV2RefType,
  ReadyForUploadFile,
  TextArea,
} from '@mint-lib/tw-components/patterns';
import { Chat } from '@mint-sdk/api-ai-assistant';
import { useSetAtom } from 'jotai';

import {
  FILE_FORMATS,
  MAX_SIZE_BYTES,
} from '../../constants/uploadingFiles.js';
import { useUpdateFileAccessMutation } from '../../queries/fileAccess.js';
import { useLoadDocumentMutation } from '../../queries/rag.js';
import { fileLocationsAtom } from '../../store/fileSystem.js';
import { ROOT_FOLDER_ID } from '../../types/fileSystem.js';
import { FileAccessType, FileAccessUser } from '../../types/rag.js';
import { ShareFilesSection } from './ShareFiles/ShareFilesSection.jsx';

type Props = {
  isOpen: boolean;
  documents: Chat.RagDocumentType[];
  onClose: () => void;
  targetFolderId?: string;
};

const UploadFileDialog = ({
  isOpen,
  onClose,
  documents,
  targetFolderId = ROOT_FOLDER_ID,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiFileSystem']);
  const queryClient = useQueryClient();
  const { queryKey: getDocumentQueryKey } = Chat.listRagDocuments({});
  const ref = useRef<FileUploaderV2RefType | null>(null);
  const { mutateAsync, isPending } = useLoadDocumentMutation();
  const { mutate: updateFileAccess } = useUpdateFileAccessMutation();

  const [uploadedFile, setUploadedFile] = useState<BaseFile[]>([]);
  const [description, setDescription] = useState('');
  const [accessType, setAccessType] = useState<FileAccessType>('invited');
  const [sharedUsers, setSharedUsers] = useState<FileAccessUser[]>([]);

  const setFileLocations = useSetAtom(fileLocationsAtom);

  const onFileSave = async (fileBlob: File) => {
    const document = await mutateAsync({
      file: fileBlob,
      description,
      targetFolderId,
    });

    if (ff.aiFileSystem.enabled) {
      updateFileAccess({
        fileId: document.fileId,
        access: { accessType, users: sharedUsers },
      });

      setFileLocations((prev) => ({
        ...prev,
        ...Object.fromEntries([[document.fileId, targetFolderId]]),
      }));
    }

    return document;
  };

  const handleUploadFile = async () => {
    if (!ref.current) return;

    const readyForUploadFiles = uploadedFile.filter(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    await ref.current.uploadFiles(readyForUploadFiles);
    await queryClient.invalidateQueries({
      queryKey: getDocumentQueryKey,
    });

    if (targetFolderId !== ROOT_FOLDER_ID) {
      enqueueSnackbar({
        variant: 'info',
        subTitle: t('Uploaded to {{rootFolder}}', {
          rootFolder: t('My Files'),
        }),
        removable: true,
        position: 'top-center',
        className: 'flex self-center',
      });
    }

    setDescription('');
    setAccessType('invited');
    setSharedUsers([]);

    onClose();
  };

  const onValidate = (file: File) => {
    const fileIndex = documents.findIndex((d) => d.filename === file.name);

    if (fileIndex !== -1) {
      return Promise.resolve({
        message: 'File name already exists',
        code: '404',
      });
    }

    return Promise.resolve(null);
  };

  const isDisabledUploadBtn = useMemo(() => {
    const areAllFilesReady = uploadedFile.some(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    return !uploadedFile.length || !areAllFilesReady;
  }, [uploadedFile]);

  return (
    <Dialog modal onOpenChange={onClose} open={isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          aria-describedby={undefined}
          size="s"
          className="max-h-[90vh]"
        >
          <DialogHeader>
            <DialogTitle>{t('Upload file')}</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex min-h-0 flex-col overflow-y-auto">
            <FileUploader
              ref={ref}
              fullWidth
              accept={FILE_FORMATS}
              validate={onValidate}
              onChange={(file) => {
                setUploadedFile(file);
              }}
              onUpload={onFileSave}
              maxFiles={10}
              maxSize={MAX_SIZE_BYTES}
            />

            <div className="mt-4 flex">
              <TextArea
                label={t('Description (Optional)')}
                placeholder={t('Insert a description')}
                onChange={({ target }) => {
                  setDescription(target.value);
                }}
                value={description}
              />
            </div>

            {ff.aiFileSystem.enabled && (
              <div className="mt-6">
                <ShareFilesSection
                  accessType={accessType}
                  onAccessTypeChange={setAccessType}
                  sharedUsers={sharedUsers}
                  onSharedUsersChange={setSharedUsers}
                />
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            {ff.aiFileSystem.enabled ? (
              <>
                <Button size="large" variant="plain" onClick={onClose}>
                  {t('Cancel')}
                </Button>
                <Button
                  size="large"
                  onClick={handleUploadFile}
                  disabled={isPending || isDisabledUploadBtn}
                >
                  {isPending && (
                    <CircularLoadingButton size="small" variant="outlined" />
                  )}
                  {t('Upload')}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleUploadFile}
                disabled={isPending || isDisabledUploadBtn}
              >
                {isPending ? (
                  <CircularLoadingButton size="small" variant="outlined" />
                ) : (
                  <AddAlt />
                )}
                {t('Confirm')}
              </Button>
            )}
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { UploadFileDialog };
